/**
 * Keeping an open tab on the release that is actually deployed.
 *
 * CenoX is one page that never reloads itself: moving between screens swaps
 * components, and the page that named this build's script is fetched once,
 * when the tab opens. A counter that leaves CenoX open all week therefore
 * keeps last week's screens after a release — the New Delivery form shipped
 * with its new rider lookup and a tab opened the day before went on showing
 * Distance and Fuel, because nothing ever asked the server again.
 *
 * So the tab asks. On opening, when it comes back into view and every few
 * minutes, it reads the deployed page and compares the build it names with
 * the one running here. When they differ, the next move to another screen is
 * a full page load instead — the natural moment, because nobody is halfway
 * through typing anything — and until then a banner says so with a button.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const BUILD_SCRIPT = /\/assets\/index-[\w-]+\.js/;
const CHECK_EVERY_MS = 5 * 60_000;

/** The build this tab is running: the entry script its own page named. */
export function runningBuild(doc: Document = document): string | null {
  for (const script of Array.from(doc.scripts)) {
    const match = script.src.match(BUILD_SCRIPT);
    if (match) return match[0];
  }
  return null;
}

/** The build the server is deploying now, or null when it cannot be told. */
export async function deployedBuild(fetcher: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetcher(`/?release-check=${Date.now()}`, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    return (await res.text()).match(BUILD_SCRIPT)?.[0] ?? null;
  } catch {
    // Offline, or a network blip: not evidence of anything.
    return null;
  }
}

/**
 * Is somebody in the middle of entering something?
 *
 * An open dialog — New Delivery is one — or the cursor in a field. Either
 * way a reload would throw away what they are typing, so it waits for the
 * next screen change instead. Only the Reload button, which they press
 * themselves, ever reloads over the top of them.
 */
export function busyWithEntry(doc: Document = document): boolean {
  if (doc.querySelector('dialog[open], [role="dialog"], [aria-modal="true"]')) return true;
  const active = doc.activeElement as HTMLElement | null;
  if (!active) return false;
  return (
    active.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)
  );
}

/** Stale only when both are known and differ. A dev server names neither. */
export function isStale(running: string | null, deployed: string | null): boolean {
  return Boolean(running && deployed && running !== deployed);
}

export function ReleaseWatcher(): ReactNode {
  const location = useLocation();
  const [stale, setStale] = useState(false);
  const running = useRef(runningBuild());
  const firstPath = useRef(location.pathname);

  useEffect(() => {
    if (!running.current) return; // development: nothing to compare
    let cancelled = false;
    const check = async () => {
      const deployed = await deployedBuild();
      if (!cancelled && isStale(running.current, deployed)) setStale(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    void check();
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Moving to another screen loads the new release, rather than the old one's
  // version of it — unless somebody is still entering something, in which
  // case the next screen change after they finish does.
  useEffect(() => {
    if (stale && location.pathname !== firstPath.current && !busyWithEntry()) window.location.reload();
    firstPath.current = location.pathname;
  }, [stale, location.pathname]);

  if (!stale) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-3 bg-slate-900 px-4 py-2.5 text-sm text-white"
      style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <span>A new version of CenoX is available.</span>
      <button
        type="button"
        className="rounded-md bg-white px-3 py-1 font-medium text-slate-900"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  );
}
